// My attendance — the whole month at a glance: present / half day / holiday +
// weekly off / leave / tour / not marked, with the kilometres travelled each day.
// Shown to every field and office user for their OWN month.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

// chip: calendar-cell look; tile: summary-card colour
const STATUS = {
  present:     { label: "Present",     short: "P",       chip: "bg-blue-600 text-white" },
  half_day:    { label: "Half day",    short: "HD",      chip: "bg-orange-500 text-white" },
  holiday:     { label: "Holiday",     short: "Holiday", chip: "bg-neutral-400 text-white" },
  weekly_off:  { label: "Weekly off",  short: "Off",     chip: "bg-neutral-400 text-white" },
  leave:       { label: "Leave",       short: "Leave",   chip: "bg-purple-600 text-white" },
  tour:        { label: "Tour",        short: "Tour",    chip: "bg-teal-600 text-white" },
  not_marked:  { label: "Not marked",  short: "A",       chip: "bg-red-100 text-red-700 border border-red-200" },
  absent:      { label: "Absent",      short: "A",       chip: "bg-red-600 text-white" },
  in_progress: { label: "In progress", short: "…",       chip: "bg-sky-100 text-sky-700 border border-sky-200" },
};

function istNow() { return new Date(Date.now() + 5.5 * 3600 * 1000); }
function currentMonth() { return istNow().toISOString().slice(0, 7); }
function shiftMonth(ym, delta) {
  const y = Number(ym.slice(0, 4)), m = Number(ym.slice(5)) - 1 + delta;
  const d = new Date(Date.UTC(y, m, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function fmtTime(iso) {
  return iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
}
function fmtHours(sec) {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export default function AttendanceCalendar() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayOpen, setDayOpen] = useState(null);
  // Managers can open a team member's calendar too ("" = my own).
  const [team, setTeam] = useState([]);
  const [viewUser, setViewUser] = useState("");
  useEffect(() => { api.get("/attendance-calendar/team").then((r) => setTeam(r.data.items || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/attendance-calendar/month", { params: { month, ...(viewUser ? { user_id: viewUser } : {}) } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [month, viewUser]);
  useEffect(() => { load(); }, [load]);

  // Sunday-first grid: blanks before the 1st, then every day of the month.
  const cells = useMemo(() => {
    if (!data) return [];
    const lead = new Date(`${data.days[0].date}T00:00:00Z`).getUTCDay();
    return [...Array(lead).fill(null), ...data.days];
  }, [data]);

  const s = data?.summary;
  const [y, m] = [Number(month.slice(0, 4)), Number(month.slice(5)) - 1];
  const tiles = s ? [
    { k: "present", label: "Present", value: s.present, cls: "bg-blue-600" },
    { k: "half", label: "Half day", value: s.half_day, cls: "bg-orange-500" },
    { k: "off", label: "Holiday / Week off", value: s.holiday_weekoff, cls: "bg-neutral-500" },
    { k: "leave", label: "Leave", value: s.leave, cls: "bg-purple-600" },
    ...(s.tour ? [{ k: "tour", label: "Tour", value: s.tour, cls: "bg-teal-600" }] : []),
    ...(data.policy ? [{ k: "absent", label: "Absent", value: s.absent, cls: "bg-red-600" }] : []),
    ...(!data.policy || s.not_marked ? [{ k: "nm", label: "Not marked", value: s.not_marked, cls: "bg-red-500" }] : []),
    ...(data.policy ? [{ k: "late", label: `Late (after ${data.policy.on_time_before})`, value: s.late_days, cls: "bg-amber-600" }] : []),
    { k: "days", label: "Days this month", value: s.days_in_month, cls: "bg-[#6D2C91]" },
    { k: "km", label: "Total km", value: s.total_km, cls: "bg-sky-700" },
  ] : [];

  return (
    <div className="p-4 pb-24 space-y-4 max-w-3xl mx-auto" data-testid="attendance-calendar-page">
      <div>
        <h1 className="text-xl font-extrabold text-neutral-900 flex items-center gap-2">
          <CalendarCheck className="h-5 w-5 text-[#6D2C91]" /> {viewUser ? "Team attendance" : "My attendance"}
        </h1>
        <p className="text-xs text-neutral-500">
          {viewUser ? `${data?.user?.name || "Team member"}'s whole month` : "Your whole month"} — tap a day for details.
        </p>
      </div>

      {team.length > 0 && (
        <select value={viewUser} onChange={(e) => { setViewUser(e.target.value); setData(null); }}
                className="w-full h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm" data-testid="att-cal-user">
          <option value="">Me</option>
          {team.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role_code}</option>)}
        </select>
      )}

      <div className="flex items-center justify-between">
        <Button variant="outline" size="icon" onClick={() => setMonth((v) => shiftMonth(v, -1))}
                data-testid="att-cal-prev" aria-label="Previous month">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-center">
          <div className="text-lg font-bold text-neutral-900" data-testid="att-cal-title">{MONTHS[m]} {y}</div>
          {month !== currentMonth() && (
            <button type="button" className="text-[11px] text-[#6D2C91] underline"
                    onClick={() => setMonth(currentMonth())} data-testid="att-cal-today">
              Back to this month
            </button>
          )}
        </div>
        <Button variant="outline" size="icon" onClick={() => setMonth((v) => shiftMonth(v, 1))}
                data-testid="att-cal-next" aria-label="Next month">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {loading && !data ? (
        <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : data && (
        <>
          <div className={"rounded-xl border border-neutral-200 overflow-hidden bg-white " + (loading ? "opacity-60" : "")}
               data-testid="att-cal-grid">
            <div className="grid grid-cols-7 bg-neutral-50 border-b border-neutral-200">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-center text-[11px] font-semibold text-neutral-600 py-1.5">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((c, i) => {
                if (!c) return <div key={`b${i}`} className="min-h-[64px] bg-neutral-50/70 border-b border-r border-neutral-100" />;
                const st = STATUS[c.status];
                const day = Number(c.date.slice(8));
                return (
                  <button
                    key={c.date} type="button"
                    onClick={() => setDayOpen(c)}
                    data-testid={`att-cal-day-${c.date}`}
                    className={"min-h-[64px] p-1 text-left border-b border-r border-neutral-100 flex flex-col gap-0.5 hover:bg-neutral-50 " +
                      (c.is_today ? "bg-amber-50" : "")}
                  >
                    <span className="text-[11px] text-neutral-500 self-end leading-none">{day}</span>
                    {st && (
                      <span className={"rounded px-1 py-0.5 text-[10px] leading-tight font-semibold truncate " + st.chip}
                            title={c.sub_status || st.label}>
                        {st.short}{c.km > 0 ? ` ${c.km.toFixed(1)}` : ""}
                      </span>
                    )}
                    {c.km > 0 && st && <span className="text-[9px] text-neutral-400 leading-none">km</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="att-cal-summary">
            {tiles.map((t) => (
              <div key={t.k} className={"rounded-lg text-white px-3 py-2.5 flex items-center justify-between gap-2 " + t.cls}
                   data-testid={`att-cal-tile-${t.k}`}>
                <span className="text-xs leading-tight">{t.label}</span>
                <span className="text-xl font-extrabold">{t.value}</span>
              </div>
            ))}
          </div>
          {data.policy ? (
            <p className="text-[11px] text-neutral-500" data-testid="att-cal-policy-note">
              {viewUser ? "Attendance is" : "Your attendance is"} based on market hours (first call to last call end): {data.policy.present_min_hours} h or more = Present,
              {" "}{data.policy.half_day_min_hours}–{data.policy.present_min_hours} h = Half day, under {data.policy.half_day_min_hours} h = Absent.
              First call after {data.policy.half_day_if_start_after} = Half day. First call after {data.policy.on_time_before} is counted as late.
            </p>
          ) : (
            <p className="text-[11px] text-neutral-400">
              A day counts as a half day if it was closed after working less than {data.half_day_below_hours} hours.
              "A" marks a past working day with no attendance.
            </p>
          )}
        </>
      )}

      <Dialog open={!!dayOpen} onOpenChange={(v) => !v && setDayOpen(null)}>
        <DialogContent className="max-w-sm" data-testid="att-cal-day-dialog">
          {dayOpen && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {new Date(`${dayOpen.date}T00:00:00`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-1.5 text-sm">
                <Row k="Status" v={dayOpen.status === "future" ? "Upcoming" : (STATUS[dayOpen.status]?.label || dayOpen.status)
                  + (dayOpen.sub_status ? ` — ${dayOpen.policy ? dayOpen.sub_status : String(dayOpen.sub_status).replace(/_/g, " ")}` : "")} />
                {dayOpen.policy ? (
                  <>
                    <Row k="First call" v={fmtTime(dayOpen.policy.first_call_ts)
                      + (dayOpen.policy.late ? ` (${dayOpen.policy.late_minutes} min late)` : "")} />
                    <Row k="Last call ended" v={fmtTime(dayOpen.policy.last_call_end_ts)} />
                    <Row k="Market hours" v={fmtHours(dayOpen.policy.market_seconds)} />
                    <Row k="Calls" v={dayOpen.policy.calls} />
                  </>
                ) : dayOpen.punch_in_ts && (
                  <>
                    <Row k="Punch in" v={fmtTime(dayOpen.punch_in_ts) + (dayOpen.is_late ? " (late)" : "")} />
                    <Row k="Punch out" v={fmtTime(dayOpen.punch_out_ts)} />
                    <Row k="Day ended" v={fmtTime(dayOpen.day_ended_ts)} />
                    <Row k="Time worked" v={fmtHours(dayOpen.working_seconds)} />
                  </>
                )}
                <Row k="Distance" v={`${dayOpen.km} km`} />
                <Row k="Visits" v={`${dayOpen.visits}${dayOpen.visits ? ` (${dayOpen.productive_visits} with order)` : ""}`} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-dashed border-neutral-200 pb-1">
      <span className="text-neutral-500">{k}</span>
      <span className="font-medium text-neutral-900 text-right">{v}</span>
    </div>
  );
}
